import React, { Fragment,  useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { useParams } from 'react-router';
import deepEqual from 'fast-deep-equal';

import Page, { Back, Title } from '../../../components/Page';
import Discount from '../../campaignsCreation/components/Discount';
import Location from '../../campaignsCreation/containers/Location';
import Audience from '../../campaignsCreation/components/Audience';
import CampaignBasicInfo from '../../campaignsCreation/components/CampaignBasicInfo';
import { EditWrapper, Success, SaveEdit } from '../../common/components';
import { validate } from '../../validation/campaignValidation';
import { LocationType, AudienceType, Campaign, DiscountItem } from '../../types';
import { State as CampaignEditState } from '../reducer';
import { LoadingOverlay } from '../../../components/Loading';
import useHelpers from '../../common/useHelpers';
import { Discount as DiscountBackendType, Campaign as CampaignBackendType } from '../../../types/discounts';
import {
    getLimitationByDiscountType,
    getRequirementsByDiscountType,
    getRewardByDiscountType
} from '../../campaignsCreation/helpers';
import { parseDiscount } from '../../utils/parseDiscountFromBackend';
import { useScopes } from '../../../auth/accessToken/Allow';
import { checkIsMccOnly, getStoreIds } from '../../helpers';

enum Stage {
    'edit',
    'success',
}

interface CampaignEditProps {
    campaignEditState: CampaignEditState;
    fetchDiscounts: (accountId: string, campaignId: string) => void;
    fetchCampaign: (accountId: string, campaignId: string) => void;
    clearFetchedData: () => void;
    updateCampaign: (accountId: string, campaign: CampaignBackendType) => void;
    updateDiscount: (accountId: string, discount: DiscountBackendType) => void;
    createDiscount: (accountId: string, discount: DiscountBackendType) => void;
    deleteDiscount: (accountId: string, discountId: string) => void;
}

const CampaignEdit = (
    {
        campaignEditState,
        fetchCampaign,
        fetchDiscounts,
        clearFetchedData,
        updateCampaign,
        updateDiscount,
        createDiscount,
        deleteDiscount,
    }: CampaignEditProps
) => {
    const { t } = useTranslation();
    const { accountId, campaignId } = useParams();
    const [initialValues, setInitialValues] = useState<Campaign | undefined>(undefined);
    const [stage, setStage] = useState<Stage>(Stage.edit);
    const isMccOnly = checkIsMccOnly(accountId);
    const scopes = useScopes();

    useEffect(() => {
        if (accountId && campaignId) {
            fetchCampaign(accountId, campaignId);
            fetchDiscounts(accountId, campaignId);
        }
        return () => clearFetchedData();
    }, [accountId, campaignId, fetchCampaign, fetchDiscounts, clearFetchedData]);

    const { campaign, discounts } = campaignEditState;
    const helpers = useHelpers();

    useEffect(() => {
        if (campaign) {
            const fromDate = new Date(discounts[0]?.requirement.purchase_from ?? 0).getTime();
            const toDate = new Date(discounts[0]?.requirement.purchase_to ?? 0).getTime();

            const allStoreIds = getStoreIds(discounts[0]) ?? [];

            const locationType =
                allStoreIds &&
                allStoreIds.length > 0 &&
                allStoreIds[0] !== '*'
                    ? LocationType.predefinedLocations
                    : LocationType.allLocations;

            const storeIds =
                allStoreIds && allStoreIds[0] !== '*'
                    ? allStoreIds
                    : [];

            const newInitialValues = {
                name: campaign?.name,
                dateFrom: new Date(fromDate),
                dateTo: new Date(toDate),
                timeFrom: new Date(fromDate),
                timeTo: new Date(toDate),
                visibleFrom:
                    new Date(discounts[0]?.visible_from ?? 0).getDate() - new Date(campaign?.created_at ?? 0).getDate(),
                discounts: discounts.map((discount) => parseDiscount(discount, helpers)),
                locationType,
                storeIds,
                audienceType: AudienceType.allAudience,
                audienceUserIds: [],
                audience: [],
                mccs: discounts[0]?.requirement?.store?.mcc || [],
                mccRanges: (discounts[0]?.requirement?.store?.mcc_ranges || []).map((range) => [
                    range.start,
                    range.end,
                ]),
            } as Campaign;

            if (!deepEqual(newInitialValues, initialValues)) {
                setInitialValues(newInitialValues);
            }
        }
    }, [campaign, discounts, helpers, initialValues]);

    const checkIfScheduledOrActive = () => {
        const currentTimestamp = new Date().getTime();
        const dateToTimestamp = new Date(initialValues?.dateTo ?? 0).getTime();
        return currentTimestamp < dateToTimestamp;
    };

    const onSubmit = (editedCampaign: Campaign) => {
        if (campaign && initialValues && accountId) {
            if (initialValues.name !== editedCampaign.name) {
                updateCampaign(accountId, {
                    campaign_id: campaign.campaign_id,
                    name: editedCampaign.name,
                    private: false,
                });
            }
            // eslint-disable-next-line array-callback-return
            editedCampaign.discounts.map((discount) => {
                const matchingDiscount = initialValues.discounts.find(
                    (initialDiscount) => initialDiscount.id === discount.id
                );
                if (matchingDiscount) {
                    if (
                        !deepEqual(matchingDiscount, discount) ||
                        !deepEqual(editedCampaign.storeIds, getStoreIds(discounts[0]))
                    ) {
                        updateDiscount(accountId, getDiscountToOperate(discount, editedCampaign));
                    }
                } else {
                    createDiscount(accountId, getDiscountToOperate(discount, editedCampaign));
                }
            });
            // eslint-disable-next-line array-callback-return
            initialValues.discounts.map((discount) => {
                const matchingDiscount = editedCampaign.discounts.find(
                    (editedDiscount) => editedDiscount.id === discount.id
                );
                if (!matchingDiscount) {
                    deleteDiscount(accountId, discount.id);
                }
            });
            setStage(Stage.success);
        }
    };

    const getDiscountToOperate = (discount: DiscountItem, editedCampaign: Campaign) => {
        const visibleFrom = new Date();
        visibleFrom.setDate(editedCampaign.dateFrom.getDate() - editedCampaign.visibleFrom);
        return {
            id: discount.id,
            active: true,
            private: false,
            campaign_id: campaign?.campaign_id ?? '',
            name: discount.title,
            visible_from: visibleFrom.toISOString(),
            description: discount.description,
            limitation: getLimitationByDiscountType(discount),
            requirement: getRequirementsByDiscountType(discount, editedCampaign),
            reward: getRewardByDiscountType(discount),
        };
    };

    if (!initialValues) {
        return <LoadingOverlay />;
    }

    if (stage === Stage.success) {
        return <Success subtitle={t('marketing.campaign.success_edit_campaign_text')} />;
    }

    return (
        <Page
            title={
                <Fragment>
                    <Back />
                    <Title>{t('marketing.campaign.campaign_edit')}</Title>
                </Fragment>
            }
        >
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validate={(values) => validate(values, t)}
                onSubmit={(values) => onSubmit(values)}
            >
                {({ values, errors, handleChange, handleBlur, handleSubmit, setFieldValue, touched }) => (
                    <form onSubmit={handleSubmit}>
                        <CardsWrapper>
                            <EditWrapper isEditable={checkIfScheduledOrActive()}>
                                <CampaignBasicInfo
                                    title={t('marketing.campaign.campaign_name_title')}
                                    subtitle={t('marketing.campaign.campaign_name_subtitle')}
                                    values={values}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    setFieldValue={setFieldValue}
                                    errors={errors}
                                    touched={touched}
                                    isEdit
                                />
                            </EditWrapper>
                            <Discount
                                values={values}
                                setFieldValue={setFieldValue}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                onNextStage={() => {}}
                                createAsset={() => {}}
                                removeAsset={() => {}}
                                isEdit
                                discountToEditCount={initialValues.discounts.length}
                                scopes={scopes}
                                assetsByDiscountId={{}}
                                isMccOnly={isMccOnly}
                            />
                            <EditWrapper isEditable={checkIfScheduledOrActive()}>
                                <CardsWrapper>
                                    <Location
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        handleBlur={handleBlur}
                                        isMccOnly={isMccOnly}
                                    />
                                    <Audience
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        onNextStage={() => {}}
                                        onPreviousStage={() => {}}
                                        isEdit
                                    />
                                </CardsWrapper>
                                <SaveEdit title={t('marketing.campaign.save_campaign')} />
                            </EditWrapper>
                        </CardsWrapper>
                    </form>
                )}
            </Formik>
        </Page>
    );
};

const CardsWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

export default CampaignEdit;
